import { upsDestinationDetails } from "utils/upsDestinationDetails";
﻿import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import Select, { components } from "react-select";
// at top of ShippingOptionsui.tsx
import {
  getActiveTaxType,
  calcShippingByType,
  type ManualShippingInput,
  type UpsShippingInput,
} from "components/Tax/manualtax";
import upslogo from "assets/images/brands/ups.png";
import { safeConsole } from "utils/envUtils";
import { handleApiError } from "utils/errorHandler";
import { showError } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";
import { readResolveTaxable } from "orderResolve/utils/readResolveTaxable";
import { forcePermissionsFalse, salesOrderUrls, sanitizeOrganizationId, resolveSellerScopeOrganizationId } from "api/salesOrderApi";
// ===== Types =====
export type AddressDetails = any; // flexible; expects .address_details with stateCode etc.
export type TaxType = "manual" | "taxcloud" | "geera";

export type ShippingRow = {
  shipping__id: string;
  shipping_name: string;
  shipping_amount: number;
  shipping_tax_percentage: number;
  shipping_tax_amount: number;
  shipping_taxable: boolean;
  shipping_delivarydate: string;
  shipping_delivery_time: string;
  /** Carrier / channel from API or set for UPS rows (e.g. "UPS") */
  shipping_type?: string;
  is_customer_pickup?: boolean;
};

/** One line sent to `/shipping/ups/rates` (dims + weight + product metadata). */
export type UpsRatesPackage = {
  name: string;
  quantity: number;
  sku: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  unit: "LBS";
};

export type ShippingOptionsV2Handle = {
  getPayload: () => {
    shipping_option: ShippingRow[];
    isUps: boolean;
    isManual: boolean;
    selected_ids: {
      manual_shipping_id: string | null;
      ups_service_code: string | null;
      integration_id: string | null;
      shipper_address_id: string | null;
    };
    ship_to: any | null;
    packages: UpsRatesPackage[];
  };
};

type CalcType = "FLAT_RATE" | "PER_ITEM" | "WEIGHT_BASED";
type WeightRange = { up_to_lbs: number; price: number };

type ManualOpt = {
  id: string;
  name: string;
  cost?: number;
  taxable?: boolean;
  description?: string;

  // NEW fields used by the logic below
  calculation_type?: CalcType;
  weight_ranges?: WeightRange[];
  above_weight?: number;
  out_of_range_message?: string;
  /** From `/shipping-costs` when the supplier marks store/customer pickup */
  is_customer_pickup?: boolean;
  shipping_type?: string;
};

type UpsRate = {
  service_code: string;
  service_name: string;
  total_charge: number | string;
  delivery_days?: number | string;
  delivery_by_time?: string;
  is_customer_pickup?: boolean;
  shipping_type?: string;
};

type UpsConfig = { id: string; name?: string; accountNumber?: string; status?: boolean;[k: string]: any };

type ShipperAddress = { id: string; company?: string; name?: string; city?: string; state?: string; zip?: string; isDefault?: boolean; status?: boolean;[k: string]: any };
type PackageDimensions = {
  length?: number | string;
  width?: number | string;
  height?: number | string;
  weight?: number | string;
  weight_type?: string; // "kg" | "g" | "lbs"
};

export type ProductLine = {
  productId: string;
  variantId: string;
  quantity: number;
  /** Display name for UPS package row (e.g. product or variant title). */
  name?: string;
  sku?: string;
  package_dimensions?: {
    length: number;
    width: number;
    height: number;
    weight: number;
    weight_type?: string;
  };
};
export type ShipSelectedIds = {
  manual_shipping_id: string | null;
  ups_service_code: string | null;
  integration_id: string | null;
  shipper_address_id: string | null;
};


type FullProduct = { product_id: string; product: any };

const UPS_ADDRESS_TYPE_OPTIONS = [
  { value: "commercial", label: "Commercial" },
  { value: "residential", label: "Residential" },
];

const UpsAddressTypeDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const upsAddressTypeSelectStyles = {
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex !important",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: "none",
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: 240,
    padding: 0,
  }),
};

// ===== Props =====

type Props = {
  token: string;
  shippingAddress: AddressDetails | null;
  totalQty: number;
  selectedProducts: ProductLine[];   // cart rows
  rateReloadKey?: string;            // changes whenever product qty/dimensions change
  productsFull: FullProduct[];       // product + variants (with package_dimensions)

  knownTaxType?: TaxType;            // if parent already knows; otherwise we call active-type
  title?: string;
  className?: string;
  readOnly?: boolean;
  createdById: string;
  supplierId?: string;
  isDisabled?: boolean;
  taxStatus?: string;
  // Optional: allow parent to control which UPS config/shipper to use
  defaultIntegrationId?: string | null;
  defaultShipperAddressId?: string | null;
  onPayloadChange?: (payload: {
    shipping_option: ShippingRow[] | null,
    isUps: boolean,
    isManual: boolean,
    selected_ids: {
      manual_shipping_id: string | null,
      ups_service_code: string | null,
      integration_id: string | null,
      shipper_address_id: string | null,
    },
    ship_to: ReturnType<typeof toUpsShipTo>,
    packages: ReturnType<typeof buildPackagesFromVariants>,
  }) => void;
  onBusyChange?: (busy: boolean) => void;
  initialSelectedIds?: {
    manual_shipping_id: string | null;
    ups_service_code: string | null;
    integration_id?: string | null;
    shipper_address_id?: string | null;
  } | null;
  // âœ… NEW: Pass saved shipping rows from order to avoid recalculation on initial load
  savedShippingRows?: ShippingRow[] | null;
};
// ===== Constants =====
const ACTIVE_TYPE_TENANT = getAuthCookie("userId") || "default";
const fmt2 = (n: any) => Number(n || 0).toFixed(2);

// ===== Helpers =====
function toLbs(w: number, unit?: string) {
  if (!Number.isFinite(w) || w <= 0) return 0;
  const u = String(unit || "").toLowerCase();
  // if (["kg", "kgs", "kilogram", "kilograms"].includes(u)) return w;
  // if (["g", "gram", "grams"].includes(u)) return w ;
  return w; // assume already LBS
}
function normId(x: any) { return String(x ?? "").trim(); }

/** True only when data explicitly says pickup â€” never assume true/false without flags. */
function pickupFromData(...vals: unknown[]): boolean {
  for (const v of vals) {
    if (v === true || v === 1) return true;
    if (typeof v === "string" && v.toLowerCase() === "true") return true;
  }
  return false;
}

/** UPS path: type + pickup from rate / tax API row only. */
function upsShippingTypeAndPickup(rate: UpsRate | null | undefined, apiRow?: Record<string, unknown> | null): Pick<ShippingRow, "shipping_type" | "is_customer_pickup"> {
  const pickup = pickupFromData(apiRow?.is_customer_pickup, rate?.is_customer_pickup);
  const st = String(apiRow?.shipping_type ?? rate?.shipping_type ?? "").trim();
  return {
    shipping_type: pickup ? st : st || "UPS",
    is_customer_pickup: pickup,
  };
}

/** Manual path: type + pickup from shipping-costs option and/or tax calc API row. */
function manualShippingTypeAndPickup(picked: ManualOpt, apiRow?: Record<string, unknown> | null): Pick<ShippingRow, "shipping_type" | "is_customer_pickup"> {
  const pickup = pickupFromData(apiRow?.is_customer_pickup, picked?.is_customer_pickup);
  const st = String(apiRow?.shipping_type ?? picked.shipping_type ?? "").trim();
  return {
    shipping_type: pickup ? st : st || "MANUAL",
    is_customer_pickup: pickup,
  };
}

function productIdOf(fp: any) {
  return normId(fp?.product_id ?? fp?.product?.id ?? fp?.product?._id);
}
function variantIdOf(v: any) {
  return normId(v?.variant_id ?? v?.id ?? v?._id);
}
function nonNegDim(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function buildPackagesFromVariants(items: ProductLine[]): UpsRatesPackage[] {
  const pkgs: UpsRatesPackage[] = [];

  for (const row of items) {
    const qty = Math.max(0, Number(row.quantity || 0));
    if (!row?.productId || !row?.variantId || qty <= 0) continue;
    const dims: PackageDimensions = (row.package_dimensions ?? {}) as PackageDimensions;
    const WT = Number(dims.weight);
    if (!Number.isFinite(WT) || WT <= 0) continue;

    const L = nonNegDim(dims.length);
    const W = nonNegDim(dims.width);
    const H = nonNegDim(dims.height);
    const WU = String(dims.weight_type || "LBS");
    const unitWeight = toLbs(WT, WU);
    const totalWeight = unitWeight;

    const name = String(row.name || "").trim() || "Product";
    const sku = String(row.sku || "").trim();

    pkgs.push({
      name,
      quantity: qty,
      sku,
      length: L,
      width: W,
      height: H,
      weight: totalWeight,
      unit: "LBS",
    });
  }

  return pkgs;
}


function toUpsShipTo(addr: any) {
  const a = addr?.address_details || addr || {};
  return {
    ...upsDestinationDetails(addr),
    address_line1: a?.addressLine1 || a?.line1 || a?.address1 || "",
    city: a?.city || "",
    state: a?.stateCode || a?.state || a?.province || "",
    postal_code: a?.zip || a?.zipcode || a?.postalCode || "",
    country_code: String(a?.countryCode || a?.country || "US").toUpperCase(),
  };
}

// helpers near top
const sumWeight = (pkgs: { weight: number }[]) => pkgs.reduce((s, p) => s + Number(p.weight || 0), 0);

function computeManualAmount(
  opt: ManualOpt,
  qty: number,
  pkgs: Array<{ weight: number }>
) {
  // console.log("computeManualAmount", opt, qty, pkgs);
  const type = String(opt?.calculation_type || "") as CalcType;

  if (type === "PER_ITEM") {
    return Number(opt?.cost || 0) * Math.max(1, Number(qty || 0));
  }
  if (type === "FLAT_RATE") {
    return Number(opt?.cost || 0);
  }

  // â”€â”€ WEIGHT_BASED (treat above_weight as WEIGHT LIMIT) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const ranges = (Array.isArray(opt?.weight_ranges) ? opt!.weight_ranges! : [])
    .map(r => ({
      up_to_lbs: Number(r?.up_to_lbs ?? 0),
      price: Number(r?.price ?? 0),
    }))
    .filter(r => r.up_to_lbs > 0)
    .sort((a, b) => a.up_to_lbs - b.up_to_lbs);

  const totalW = (pkgs || []).reduce((s, p) => s + Number(p?.weight || 0), 0);
  const hit = ranges.find(r => totalW <= r.up_to_lbs);
  if (hit) return Number(hit.price || 0);
  // console.log("WEIGHT_BASED: no hit for totalW=", hit, "ranges=", ranges);
  // Past the highest bucket
  const last = ranges[ranges.length - 1];
  // NOTE: backend field name is misleading: treat as weight LIMIT
  const limit = Number(opt?.above_weight ?? 0); // actually a WEIGHT threshold
  // console.log("WEIGHT_BASED: totalW=", totalW, "limit=", limit);
  // If still under or equal to the limit, use the last bucket price
  if (limit > 0 && totalW <= limit) {
    return last ? Number(last.price || 0) : 0;
  }

  // Over the limit: keep last bucket price (UI will show note)
  return last ? Number(last.price || 0) : 0;
}



const ShippingOptionsui = forwardRef<ShippingOptionsV2Handle, Props>((props, ref) => {
  const {
    token,
    shippingAddress,
    totalQty,
    selectedProducts,
    rateReloadKey,
    productsFull,
    knownTaxType,
    title = "Shipping Options",
    className,
    readOnly,
    createdById,
    supplierId,
    defaultIntegrationId = null,
    defaultShipperAddressId = null,
    isDisabled,
    initialSelectedIds,
    savedShippingRows,  // âœ… NEW: Saved shipping data from parent
  } = props;
  // console.log("ShippingOptionsui propsinsidess=", props);

  const API = process.env.REACT_APP_API_URL as string;
  const sellerOrganizationId = useMemo(
    () => sanitizeOrganizationId(resolveSellerScopeOrganizationId(supplierId)),
    [supplierId]
  );
  const onBusyChangeRef = React.useRef(props.onBusyChange);
  const onPayloadChangeRef = React.useRef(props.onPayloadChange);

  React.useEffect(() => {
    onBusyChangeRef.current = props.onBusyChange;
    onPayloadChangeRef.current = props.onPayloadChange;
  });

  // useEffect(() => {
  //  console.log("ShippingOptionsui: savedShippingRows=", supplierId);
  // }, [supplierId]);

  // â€”â€”â€” state (no localStorage) â€”â€”â€”
  const [manual, setManual] = useState<ManualOpt[]>([]);
  const [upsRates, setUpsRates] = useState<UpsRate[]>([]);
  /** True while POST `/shipping/ups/rates` is in flight (UPS rows only). */
  const [upsRatesLoading, setUpsRatesLoading] = useState(false);
  /** True after the latest UPS rates request for the current inputs finished (success or error). */
  const [upsRatesAttempted, setUpsRatesAttempted] = useState(false);
  const [rows, setRows] = useState<ShippingRow[] | null>(null);
  const [upsConfigs, setUpsConfigs] = useState<UpsConfig[]>([]);
  const [shipperAddresses, setShipperAddresses] = useState<ShipperAddress[]>([]);
  const [integrationId, setIntegrationId] = useState<string | null>(defaultIntegrationId);
  const [shipperId, setShipperId] = useState<string | null>(defaultShipperAddressId);

  const [selectedManualId, setSelectedManualId] = useState<string | null>(null);
  const [selectedUpsCode, setSelectedUpsCode] = useState<string | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<"manual" | "ups" | null>(null);
  const [getstatusTaxType, setGetStatusTaxType] = React.useState("");
  const [taxType, setTaxType] = useState<TaxType | null>(knownTaxType ?? null);
  const [aboveNote, setAboveNote] = useState<string | null>(null);
  // const [packages, setPackages] = React.useState<
  //   Array<{ length: number; width: number; height: number; weight: number; unit: "LBS" }>
  // >([]);
  // inside component:
  const [selectedIds, setSelectedIds] = useState({
    manual_shipping_id: null as string | null,
    ups_service_code: null as string | null,
    integration_id: null as string | null,
    shipper_address_id: null as string | null,
  });


  // NEW refs â€“ add near other state
  const didApplyInitial = React.useRef(false);
  const userTouchedSelection = React.useRef(false);

  // Whenever user clicks a radio, mark as touched
  // (put inside both manual & UPS onClick handlers)
  userTouchedSelection.current = true;


  const addressKey = JSON.stringify(toUpsShipTo(shippingAddress || {}));
  const [upsAddressType, setUpsAddressType] = useState<{ key: string; value: string } | null>(null);
  const addressType = upsAddressType?.key === addressKey ? upsAddressType.value : "saved";
  const ship_to = useMemo(() => ({
    ...toUpsShipTo(shippingAddress || {}),
    ...(addressType === "saved" ? {} : { is_residential: addressType === "residential" }),
  }), [shippingAddress, addressType]);
  const upsRequestRef = React.useRef<AbortController | null>(null);
  useEffect(() => () => { upsRequestRef.current?.abort(); }, []);
  const qty = useMemo(() => Number(totalQty || 0), [totalQty]);
  const packages = useMemo(() => buildPackagesFromVariants(selectedProducts), [selectedProducts, qty]);

  /** Sum shippable weight (unit weight Ã— line quantity) for UPS eligibility. */
  const totalPackageUnitWeight = useMemo(
    () => packages.reduce((s, p) => s + Number(p.weight || 0), 0),
    [packages]
  );
// console.log(totalPackageUnitWeight,"totalPackageUnitWeight");
// console.log(qty,"qty");
// console.log(integrationId,"integrationId");
// console.log(shipperId,"shipperId");
// console.log(ship_to.address_line1,"ship_to.address_line1");
// console.log(ship_to.city,"ship_to.city");
// console.log(ship_to.state,"ship_to.state");
// console.log(ship_to.postal_code,"ship_to.postal_code");
// console.log(packages,"packages");
  const canCallRates = useMemo(() => {
    const addrOk =
      !!integrationId &&
      !!shipperId &&
      !!ship_to.address_line1 &&
      !!ship_to.city &&
      !!ship_to.state &&
      !!ship_to.postal_code;
    // Only block UPS when there is no shippable qty or no positive weight; L/W/H may be 0.
    return addrOk && qty > 0 && totalPackageUnitWeight > 0;
  }, [
    integrationId,
    shipperId,
    ship_to.address_line1,
    ship_to.city,
    ship_to.state,
    ship_to.postal_code,
    qty,
    totalPackageUnitWeight,
  ]);

  // âœ… Cache to prevent repeated API calls
  const taxTypeCache = React.useRef<{ value: TaxType | null; fetched: boolean }>({ value: null, fetched: false });

  const resolveTaxType = useCallback(async (): Promise<TaxType> => {
    // âœ… Return cached value if already fetched
    if (taxTypeCache.current.fetched) {
      // console.log("[SHIP:TaxType] Using cached value:", taxTypeCache.current.value);
      return taxTypeCache.current.value || (knownTaxType ?? "manual");
    }

    if (knownTaxType) {
      // console.log("[SHIP:TaxType] Using known type:", knownTaxType);
      taxTypeCache.current = { value: knownTaxType, fetched: true };
      return knownTaxType;
    }

    if (!sellerOrganizationId) {
      safeConsole.warn("[SHIP:TaxType] No seller organization id, using manual");
      taxTypeCache.current = { value: "manual", fetched: true };
      return "manual";
    }

    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${process.env.REACT_APP_API_URL}${forcePermissionsFalse(`/organizations/${sellerOrganizationId}/tax/active-type`)}`;
    
    try {
      const res = await getActiveTaxType(token, sellerOrganizationId);
      // console.log("[SHIP:TaxType] API returned:", res);
      const type = (res as TaxType) || "manual";
      setGetStatusTaxType(res);
      taxTypeCache.current = { value: type, fetched: true };
      return type;
    } catch (e) {
      safeConsole.warn("[SHIP:TaxType] API failed, using manual", e);
      await handleApiError(e, {
        module: "other",
        action: "ShippingOptionsui_get_active_tax_type",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "GET",
        additionalInfo: {
          supplierId,
          createdById,
        },
      }, token);
      taxTypeCache.current = { value: "manual", fetched: true };
      return "manual";
    }
  }, [token, sellerOrganizationId, knownTaxType]);

  const loadManual = useCallback(async () => {
    if (!sellerOrganizationId) {
      setManual([]);
      return;
    }
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}${forcePermissionsFalse(salesOrderUrls.shippingCosts(sellerOrganizationId))}`;
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    
    try {
      onBusyChangeRef.current?.(true);
      const r = await fetch(url, { headers });
      if (!r.ok) return setManual([]);
      const j = await r.json().catch(() => []);
      const arr = Array.isArray(j) ? j : j?.data ?? [];
      setManual(
        arr
          .filter((x: any) => x?.status !== false)
          .map((x: any) => ({
            id: String(x._id || x.id),
            name: String(x.name || "Manual Shipping"),
            cost: Number(x.cost || 0),
            taxable: readResolveTaxable(x, ["taxable", "is_taxable"], true),
            description: x?.description || "",
            calculation_type: x?.calculation_type,
            weight_ranges: x?.weight_ranges || [],
            above_weight: x?.above_weight,
            out_of_range_message: x?.out_of_range_message,
            is_customer_pickup: x?.is_customer_pickup === true,
            shipping_type: String(x?.shipping_type ?? "").trim(),
          }))
      );
      // setManual(arr.filter((x: any) => x?.status !== false).map((x: any) => ({ id: String(x._id || x.id), name: String(x.name || "Manual Shipping"), cost: Number(x.cost || 0), taxable: x?.taxable !== false, description: x?.description || "" })));
    } catch (error) {
      setManual([]);
      await handleApiError(error, {
        module: "other",
        action: "ShippingOptionsui_load_manual",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "GET",
        additionalInfo: {
          supplierId,
          createdById,
        },
      }, token);
    } finally { onBusyChangeRef.current?.(false); }
  }, [API, token, sellerOrganizationId]);

  const loadUpsConfigs = useCallback(async () => {
    if (!sellerOrganizationId) {
      setUpsConfigs([]);
      return;
    }
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}${forcePermissionsFalse(`/shipping/ups/organizations/${sellerOrganizationId}/configs`)}`;
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    
    try {
      onBusyChangeRef.current?.(true);
      const r = await fetch(url, { headers });
      const data = r.ok ? await r.json().catch(() => []) : [];
      const list: UpsConfig[] = (Array.isArray(data) ? data : data?.data ?? []).map((c: any) => ({ id: String(c.id || c._id || c.integration_id || ""), ...c }));
      setUpsConfigs(list);
      if (!integrationId) setIntegrationId(list[0]?.id || null);
    } catch (error) {
      setUpsConfigs([]);
      await handleApiError(error, {
        module: "other",
        action: "ShippingOptionsui_load_ups_configs",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "GET",
        additionalInfo: {
          supplierId,
          createdById,
        },
      }, token);
    } finally { onBusyChangeRef.current?.(false); }
  }, [API, token, integrationId, sellerOrganizationId]);

  const loadShippers = useCallback(async () => {
    if (!sellerOrganizationId) {
      setShipperAddresses([]);
      return;
    }
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}${forcePermissionsFalse(`/shipping/ups/organizations/${sellerOrganizationId}/shipper-addresses`)}`;
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    
    try {
      onBusyChangeRef.current?.(true);
      const r = await fetch(url, { headers });
      const data = r.ok ? await r.json().catch(() => []) : [];

      const list: ShipperAddress[] = (Array.isArray(data) ? data : data?.data ?? []).map((s: any) => ({ id: String(s.id || s._id || s.shipper_address_id || ""), ...s }));
      setShipperAddresses(list);
      if (!shipperId) setShipperId(list[0]?.id || null);
    } catch (error) {
      setShipperAddresses([]);
      await handleApiError(error, {
        module: "other",
        action: "ShippingOptionsui_load_shippers",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "GET",
        additionalInfo: {
          supplierId,
          createdById,
        },
      }, token);
    } finally { onBusyChangeRef.current?.(false); }
  }, [API, token, shipperId, sellerOrganizationId]);

  const loadUpsRates = useCallback(async () => {
    upsRequestRef.current?.abort();
    const controller = new AbortController();
    upsRequestRef.current = controller;
    if (!canCallRates) {
      setUpsRates([]);
      setUpsRatesLoading(false);
      setUpsRatesAttempted(false);
      onBusyChangeRef.current?.(false);
      return;
    }
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const url = `${API}/shipping/ups/rates`;
    const payload = { integration_id: integrationId, shipper_address_id: shipperId, packing_mode: "configured_boxes", ship_to, packages } as any;
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    
    try {
      setUpsRatesLoading(true);
      setUpsRatesAttempted(false);
      onBusyChangeRef.current?.(true);
      const r = await fetch(url, { method: "POST", headers, body: JSON.stringify(payload), signal: controller.signal });
      const raw = await r.json().catch(() => ({}));
      if (controller.signal.aborted) return;
      if (!r.ok) {
        const msg = String(
          (raw as any)?.error || (raw as any)?.message || `UPS rates request failed (${r.status})`
        ).trim();
        showError(msg || "UPS rates request failed");
        setUpsRates([]);
        await handleApiError(new Error(msg || `HTTP ${r.status}`), {
          module: "other",
          action: "ShippingOptionsui_load_ups_rates",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url,
          method: "POST",
          requestBody: payload,
          additionalInfo: {
            supplierId,
            createdById,
            integrationId,
            shipperId,
            status: r.status,
          },
        }, token);
        return;
      }
      const data = raw;
      const list: UpsRate[] = (Array.isArray(data) ? data : data?.data ?? []);
      setUpsRates(list);
      // preselect the first ups service (we show both manual and ups lists simultaneously)
      // if (!selectedUpsCode && list[0]?.service_code) setSelectedUpsCode(String(list[0].service_code));
      // AFTER
      if (
        !userTouchedSelection.current &&                         // ðŸ‘ˆ don't override user
        !selectedCarrier &&                                      // ðŸ‘ˆ only when nothing chosen yet
        !selectedManualId && !selectedUpsCode && list[0]?.service_code
      ) {
        setSelectedUpsCode(String(list[0].service_code));
        setSelectedCarrier("ups");
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      setUpsRates([]);
      const msg = error instanceof Error ? error.message : "Failed to load UPS rates";
      showError(msg);
      await handleApiError(error, {
        module: "other",
        action: "ShippingOptionsui_load_ups_rates",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url,
        method: "POST",
        requestBody: payload,
        additionalInfo: {
          supplierId,
          createdById,
          integrationId,
          shipperId,
        },
      }, token);
    } finally {
      if (!controller.signal.aborted) {
      setUpsRatesLoading(false);
      setUpsRatesAttempted(true);
      onBusyChangeRef.current?.(false);
      }
    }
  }, [API, token, canCallRates, integrationId, shipperId, ship_to, packages, selectedUpsCode]);

  // âœ… Boot once: load both lists and tax type
  const didBootRef = React.useRef(false);
  useEffect(() => {
    if (didBootRef.current) return; // âœ… Run only once
    didBootRef.current = true;

    (async () => {
      setRows(null);
      if (!didApplyInitial.current) {
        setSelectedCarrier(null);
      }
      await Promise.all([loadManual(), loadUpsConfigs(), loadShippers()]);
      const t = await resolveTaxType();
      setTaxType(t);
    })();
  }, [loadManual, loadUpsConfigs, loadShippers, resolveTaxType]);

  // âœ… Re-run UPS /rates only when relevant inputs change (use stable keys)
  const ratesKey = useMemo(() => JSON.stringify({
    canCall: canCallRates,
    integId: integrationId,
    shipId: shipperId,
    shipTo: ship_to,
    pkgs: packages,
    qty,
    reloadKey: rateReloadKey,
  }), [canCallRates, integrationId, shipperId, ship_to, packages, qty, rateReloadKey]);

  const lastRatesKeyRef = React.useRef("");
  useEffect(() => {
    if (lastRatesKeyRef.current === ratesKey) return;
    lastRatesKeyRef.current = ratesKey;
    setUpsRates([]);
    setRows(null);
    setUpsRatesAttempted(false);
    loadUpsRates();
  }, [ratesKey, loadUpsRates]);

  // âœ… Stable key for row computation - includes actual package data to detect real changes
  const selectedUpsCharge =
    selectedCarrier === "ups" && selectedUpsCode
      ? Number(
          upsRates.find((u) => String(u.service_code) === String(selectedUpsCode))
            ?.total_charge ?? 0
        )
      : 0;

  const rowsComputeKey = useMemo(() => JSON.stringify({
    carrier: selectedCarrier,
    manualId: selectedManualId,
    upsCode: selectedUpsCode,
    upsCharge: selectedUpsCharge,
    qty,
    // âœ… Include actual package data (weight, dimensions) to detect real changes
    pkgs: packages.map(p => ({
      q: Number(p.quantity || 0),
      w: Number(p.weight || 0),
      l: Number(p.length || 0),
      wd: Number(p.width || 0),
      h: Number(p.height || 0),
    })),
    // âœ… Include full address details to detect address changes
    addr: shippingAddress?.address_details ? {
      line1: shippingAddress.address_details.addressLine1,
      city: shippingAddress.address_details.city,
      state: shippingAddress.address_details.state,
      zip: shippingAddress.address_details.zip,
    } : null,
  }), [selectedCarrier, selectedManualId, selectedUpsCode, selectedUpsCharge, qty, packages, shippingAddress]);

  const lastRowsKeyRef = React.useRef("");

  // âœ… Track if this is the initial load from saved order data (skip API call)
  const isInitialLoadRef = React.useRef(true);

  // âœ… Compute rows when selection or key inputs change
  useEffect(() => {
    if (lastRowsKeyRef.current === rowsComputeKey) {
      // safeConsole.log("[SHIP] Rows already computed for this key, skipping");
      return;
    }
    lastRowsKeyRef.current = rowsComputeKey;

    (async () => {
      if (!selectedCarrier) { setRows(null); return; }

      // âœ… Skip API call on initial load if we have initialSelectedIds (Edit mode with saved data)
      // After initial load, set flag to false so subsequent changes trigger API calls
      const skipApiOnInitialLoad = isInitialLoadRef.current && initialSelectedIds;
      if (isInitialLoadRef.current) {
        // safeConsole.log("[SHIP] Initial load detected, setting flag to false for future changes");
        isInitialLoadRef.current = false;
      }

      // keep your existing resolve, but we also use the explicit status getter you provided
      const _taxType = taxType ?? (await resolveTaxType());

      // ðŸ‘‡ your exact lines (do not change)
      const taxTypeFromStatus = String(getstatusTaxType ?? "").trim().toLowerCase();
      const isReady = taxTypeFromStatus == "manual";

      // ============ MANUAL ============
      if (selectedCarrier === "manual" && selectedManualId) {
        const picked = manual.find(m => m.id === selectedManualId);
        if (!picked) { setRows(null); return; }
        const amount = computeManualAmount(picked, qty, packages);
        // show above-weight note (display-only) when WEIGHT_BASED goes over top range
        if (String(picked.calculation_type).toUpperCase() === "WEIGHT_BASED") {
          const totalW = sumWeight(packages);
          const maxRange = Math.max(...(picked.weight_ranges || []).map(r => Number(r.up_to_lbs || 0)), 0);
          if (totalW > maxRange && Number(picked.above_weight || 0) > 0 && picked.out_of_range_message) {
            setAboveNote(String(picked.out_of_range_message));
          } else {
            setAboveNote(null);
          }
        } else {
          setAboveNote(null);
        }

        // âœ… Skip API call on initial load OR when tax not ready; use fallback
        if (skipApiOnInitialLoad || !isReady || !token || !shippingAddress) {
          // console.log("[SHIP:MANUAL] Skipping API call:", { skipApiOnInitialLoad, isReady, hasToken: !!token, hasAddress: !!shippingAddress });

          // âœ… Use saved data from parent if available (Edit mode), otherwise fallback to zero tax
          if (skipApiOnInitialLoad && savedShippingRows && savedShippingRows.length > 0) {
            // safeConsole.log("[SHIP:MANUAL] Using saved shipping data from parent:", savedShippingRows);
            setRows(savedShippingRows);
            return;
          }

          // Fallback: no saved data or not initial load
          setRows([{
            shipping__id: picked.id,
            shipping_name: picked.name,
            shipping_amount: amount,
            shipping_tax_percentage: 0,
            shipping_tax_amount: 0,
            shipping_taxable: readResolveTaxable(picked as Record<string, unknown>, ["taxable", "is_taxable"], true),
            shipping_delivarydate: "",
            shipping_delivery_time: "",
            ...manualShippingTypeAndPickup(picked, null),
          }]);
          return;
        }

        const userId = getAuthCookie("userId");
        const licenseType = getAuthCookie("licenseType");
        const userName = getAuthCookie("userName");
        const manualInput: ManualShippingInput = {
          shippingOption: picked,
          address: shippingAddress,
          quantity: qty,
          createdById: sellerOrganizationId,
          supplierId: sellerOrganizationId,
        };
        
        try {
          // console.log("[SHIP:MANUAL] Calling calcShippingByType API (user changed values)");
          onBusyChangeRef.current?.(true);

          const result = await calcShippingByType(token, _taxType, {
            kind: "manual",
            input: manualInput,
          });

          // start from API result (if any), but ALWAYS override with the live amount
          const amount = computeManualAmount(picked, qty, packages);

          // shape a row no matter what the API returns
          const baseRow = result && result.shipping_name
            ? { ...result }
            : {
              shipping__id: picked.id,
              shipping_name: picked.name,
              shipping_amount: 0,
              shipping_tax_percentage: 0,
              shipping_tax_amount: 0,
              shipping_taxable: readResolveTaxable(picked as Record<string, unknown>, ["taxable", "is_taxable"], true),
              shipping_delivarydate: "",
              shipping_delivery_time: "",
            };

          baseRow.shipping_amount = Number(amount);

          // if API provided a percentage and this method is taxable, recompute tax on the overridden amount
          if (
            baseRow?.shipping_taxable !== false &&
            typeof baseRow?.shipping_tax_percentage === "number" &&
            baseRow.shipping_tax_percentage > 0
          ) {
            baseRow.shipping_tax_amount = Number(
              ((baseRow.shipping_tax_percentage / 100) * amount).toFixed(2)
            );
          } else {
            baseRow.shipping_tax_amount = 0;
          }

          const metaM = manualShippingTypeAndPickup(picked, baseRow as Record<string, unknown>);
          setRows([{ ...baseRow, ...metaM }]);
        } catch (error) {
          // fallback: still use computed amount
          const amount = computeManualAmount(picked, qty, packages);
          setRows([{
            shipping__id: picked.id,
            shipping_name: picked.name,
            shipping_amount: Number(amount),
            shipping_tax_percentage: 0,
            shipping_tax_amount: 0,
            shipping_taxable: readResolveTaxable(picked as Record<string, unknown>, ["taxable", "is_taxable"], true),
            shipping_delivarydate: "",
            shipping_delivery_time: "",
            ...manualShippingTypeAndPickup(picked, null),
          }]);
          await handleApiError(error, {
            module: "other",
            action: "ShippingOptionsui_calc_shipping_by_type_manual",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${API}/shipping/calc`,
            method: "POST",
            requestBody: {
              kind: "manual",
              input: manualInput,
            },
            additionalInfo: {
              taxType: _taxType,
              supplierId,
              createdById,
            },
          }, token);
        } finally {
          onBusyChangeRef.current?.(false);
        }

        return;
      }

      // ============ UPS ============
      if (selectedCarrier === "ups" && selectedUpsCode) {
        const rate = upsRates.find(u => String(u.service_code) === String(selectedUpsCode));
        if (!rate) { setRows(null); return; }

        // âœ… Skip API call on initial load OR when tax not ready; use fallback
        if (skipApiOnInitialLoad || !isReady || !token || !shippingAddress) {
          // console.log("[SHIP:UPS] Skipping API call:", { skipApiOnInitialLoad, isReady, hasToken: !!token, hasAddress: !!shippingAddress });

          // âœ… Use saved data from parent if available (Edit mode), otherwise fallback to zero tax
          if (skipApiOnInitialLoad && savedShippingRows && savedShippingRows.length > 0) {
            safeConsole.log("[SHIP:UPS] Using saved shipping data from parent:", savedShippingRows);
            setRows(savedShippingRows);
            return;
          }

          // Fallback: no saved data or not initial load
          const amount = Number(rate?.total_charge ?? 0);
          setRows([{
            shipping__id: String(rate?.service_code || ""),
            shipping_name: String(rate?.service_name || ""),
            shipping_amount: amount,
            shipping_tax_percentage: 0,
            shipping_tax_amount: 0,
            shipping_taxable: true,
            shipping_delivarydate: String(rate?.delivery_days || ""),
            shipping_delivery_time: String(rate?.delivery_by_time || ""),
            ...upsShippingTypeAndPickup(rate, null),
          }]);
          return;
        }

        const userId = getAuthCookie("userId");
        const licenseType = getAuthCookie("licenseType");
        const userName = getAuthCookie("userName");
        const upsInput: UpsShippingInput = {
          upsRate: rate,
          address: shippingAddress,
          quantity: qty,
          createdById,
          integrationId,
          supplierId,
        };
        
        try {
          // console.log("[SHIP:UPS] Calling calcShippingByType API (user changed values)");
          onBusyChangeRef.current?.(true); // âœ… Set busy before API call
          const result = await calcShippingByType(token, _taxType, {
            kind: "ups",
            input: upsInput,
          });

          const resAny = result as Record<string, unknown>;
          const metaU = upsShippingTypeAndPickup(rate, resAny);
          setRows([{ ...(result as ShippingRow), ...metaU }]);
        } catch (error) {
          const amount = Number(rate?.total_charge ?? 0);
          setRows([{
            shipping__id: String(rate?.service_code || ""),
            shipping_name: String(rate?.service_name || ""),
            shipping_amount: amount,
            shipping_tax_percentage: 0,
            shipping_tax_amount: 0,
            shipping_taxable: true,
            shipping_delivarydate: String(rate?.delivery_days || ""),
            shipping_delivery_time: String(rate?.delivery_by_time || ""),
            ...upsShippingTypeAndPickup(rate, null),
          }]);
          await handleApiError(error, {
            module: "other",
            action: "ShippingOptionsui_calc_shipping_by_type_ups",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${API}/shipping/calc`,
            method: "POST",
            requestBody: {
              kind: "ups",
              input: upsInput,
            },
            additionalInfo: {
              taxType: _taxType,
              supplierId,
              createdById,
              integrationId,
            },
          }, token);
        } finally {
          onBusyChangeRef.current?.(false); // âœ… Clear busy after API call
        }
        return;
      }
    })();
  }, [rowsComputeKey, manual, upsRates, taxType, resolveTaxType, getstatusTaxType, API, token, integrationId, shipperId, createdById, supplierId]);

  // â€”â€”â€” expose payload â€”â€”â€”
  useImperativeHandle(ref, () => ({
    getPayload: () => ({
      shipping_option: rows ?? [],
      isUps: selectedCarrier === "ups",
      isManual: selectedCarrier === "manual",
      selected_ids: {
        manual_shipping_id: selectedCarrier === "manual" ? selectedManualId : null,
        ups_service_code: selectedCarrier === "ups" ? selectedUpsCode : null,
        integration_id: integrationId,
        shipper_address_id: shipperId,
      },
      ship_to,
      packages,
    }),

  }), [rows, selectedCarrier, selectedManualId, selectedUpsCode, integrationId, shipperId, ship_to, packages]);

  const payloadMemo = useMemo(() => ({
    shipping_option: rows ?? [],
    isUps: selectedCarrier === "ups",
    isManual: selectedCarrier === "manual",
    selected_ids: {
      manual_shipping_id: selectedCarrier === "manual" ? selectedManualId : null,
      ups_service_code: selectedCarrier === "ups" ? selectedUpsCode : null,
      integration_id: integrationId,
      shipper_address_id: shipperId,
    },
    ship_to,
    packages,
  }), [rows, selectedCarrier, selectedManualId, selectedUpsCode, integrationId, shipperId, ship_to, packages]);

  const payloadKey = useMemo(() => JSON.stringify({
    rows: (payloadMemo.shipping_option || []).map((r: any) => [
      r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount
    ]),
    ids: payloadMemo.selected_ids,
    ups: payloadMemo.isUps,
    man: payloadMemo.isManual,
  }), [payloadMemo]);

  const lastKeyRef = React.useRef("");
  useEffect(() => {
    if (!onPayloadChangeRef.current) return;
    if (lastKeyRef.current === payloadKey) return;
    lastKeyRef.current = payloadKey;
    onPayloadChangeRef.current(payloadMemo);
  }, [payloadKey, payloadMemo]);


  // const applyInitialSelection = useCallback(() => {
  //   const init = initialSelectedIds;
  //   if (!init) return;

  //   if (init.manual_shipping_id && manual.length) {
  //     const id = String(init.manual_shipping_id);
  //     if (manual.some(m => m.id === id)) {
  //       setSelectedCarrier("manual");
  //       setSelectedManualId(id);
  //       setSelectedUpsCode(null);
  //       setSelectedIds(s => ({ ...s, manual_shipping_id: id, ups_service_code: null }));
  //       return;
  //     }
  //   }

  //   if (init.ups_service_code && upsRates.length) {
  //     const code = String(init.ups_service_code);
  //     if (upsRates.some(r => String(r.service_code) === code)) {
  //       setSelectedCarrier("ups");
  //       setSelectedUpsCode(code);
  //       setSelectedManualId(null);
  //       setSelectedIds(s => ({ ...s, manual_shipping_id: null, ups_service_code: code }));
  //     }
  //   }
  // }, [initialSelectedIds, manual, upsRates]);

  const applyInitialSelection = useCallback(() => {
    if (didApplyInitial.current) {
      safeConsole.log("[SHIP] Already applied initial selection, skipping");
      return;
    }

    const init = initialSelectedIds;
    // console.log("[SHIP] applyInitialSelection called with:", init);
    // console.log("[SHIP] Available manual options:", manual.length, manual.map(m => m.id));
    // console.log("[SHIP] Available UPS rates:", upsRates.length, upsRates.map(r => r.service_code));

    if (!init) {
      safeConsole.log("[SHIP] No initial selection provided");
      return;
    }

    if (init.manual_shipping_id && manual.length) {
      const id = String(init.manual_shipping_id);
      // console.log("[SHIP] Checking for manual ID:", id);
      if (manual.some(m => m.id === id)) {
        // console.log("[SHIP] âœ… Applying manual selection:", id);
        didApplyInitial.current = true;
        setSelectedCarrier("manual");
        setSelectedManualId(id);
        setSelectedUpsCode(null);
        setSelectedIds(s => ({ ...s, manual_shipping_id: id, ups_service_code: null }));
        return;
      } else {
        safeConsole.log("[SHIP] âŒ Manual ID not found in available options");
      }
    }

    if (init.ups_service_code && upsRates.length) {
      const code = String(init.ups_service_code);
      // console.log("[SHIP] Checking for UPS code:", code);
      if (upsRates.some(r => String(r.service_code) === code)) {
        // console.log("[SHIP] âœ… Applying UPS selection:", code);
        didApplyInitial.current = true;
        setSelectedCarrier("ups");
        setSelectedUpsCode(code);
        setSelectedManualId(null);
        setSelectedIds(s => ({ ...s, manual_shipping_id: null, ups_service_code: code }));
      } else {
        safeConsole.log("[SHIP] âŒ UPS code not found in available rates");
      }
    }
  }, [initialSelectedIds, manual, upsRates]);

  // Run once after both lists are present (either could be empty)
  useEffect(() => {
    applyInitialSelection();
    // intentionally NO other deps (we control it via the lock)
  }, [applyInitialSelection]);

  // âœ… Apply initial selection after lists are loaded (runs once with lock)
  useEffect(() => {
    // Wait until we have at least one list loaded
    if (manual.length === 0 && upsRates.length === 0) return;
    applyInitialSelection();
  }, [manual.length, upsRates.length, applyInitialSelection]);


  // â€”â€”â€” UI â€”â€”â€” (always show BOTH lists) â€”â€”â€”
  return (
    <div className={className} aria-disabled={isDisabled}>
      <div className="card shadow-border-3 box-shadow position-relative">
        {/* hard click blocker */}
        {isDisabled && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              // zIndex: 9999,            // ensure above everything
              background: "transparent",
              cursor: "not-allowed",
            }}
          />
        )}

        <div
          className="card-header bg-light"
          style={
            isDisabled
              ? { opacity: 0.7, pointerEvents: "none" } // block interactions
              : undefined
          }
        >
          <h5 className="align-items-center card-title d-flex mb-0">
            <i className="ri-truck-line align-bottom me-2 fs-2" style={{ color: "#00a63e" }} />
            {title}
             <span className="text-danger">*</span>
          </h5>
        </div>

        <div
          className="card-body"
          style={
            isDisabled
              ? { opacity: 0.7, pointerEvents: "none" } // block ALL child inputs/labels
              : undefined
          }
        >
          <div className="mb-3 text-muted">Choose Shipping Method</div>

          {/* Manual options */}
          {/* {manual.map((m) => (
            <label
              key={m.id}
              className={`border rounded-4 p-3 mb-2 w-100 d-flex justify-content-between align-items-center ${selectedCarrier === "manual" && selectedManualId === m.id ? "border-primary bg-light" : ""
                }`}
            >
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  className="form-check-input me-3"
                  name="shipping_method"
                  checked={selectedCarrier === "manual" && selectedManualId === m.id}
                  onClick={() => {
                    if (readOnly || isDisabled) return;
                    setSelectedCarrier("manual");
                    setSelectedManualId(m.id);
                    setSelectedUpsCode(null);
                    setSelectedIds((s) => ({ ...s, manual_shipping_id: m.id, ups_service_code: null }));
                  }}
                  disabled={readOnly || isDisabled}
                />
                <div>
                  <div className="fw-semibold">{m.name}</div>
                  {selectedCarrier === "manual" && selectedManualId === m.id && aboveNote && (
                    <div className="small text-muted mt-1">{aboveNote}</div>
                  )}
                </div>
              </div>
              <div className="fw-bold"></div>
            </label>
          ))} */}
          {manual.map((m) => {
            const limit = Number(m.above_weight || 0);
            const totalW = sumWeight(packages);
            const overLimit = limit > 0 && totalW > limit;

            if (overLimit) {
              // non-selectable card with the out-of-range message
              return (
                <div
                  key={m.id}
                  className="border rounded-4 p-3 mb-2 w-100 d-flex justify-content-between align-items-center text-muted"
                >
                  <div className="d-flex align-items-center">
                    {/* no radio */}
                    <div>
                      <div className="fw-semibold">{m.name}</div>
                      {m.out_of_range_message && (
                        <div className="small text-danger mt-1">{m.out_of_range_message}</div>
                      )}
                    </div>
                  </div>
                  <div className="fw-bold"></div>
                </div>
              );
            }

            // normal selectable row
            return (
              <label
                key={m.id}
                className={`border rounded-4 p-3 mb-2 w-100 d-flex justify-content-between align-items-center ${selectedCarrier === "manual" && selectedManualId === m.id ? "border-primary bg-light" : ""
                  }`}
              >
                <div className="d-flex align-items-center">
                  <input
                    type="radio"
                    className="form-check-input me-3"
                    name="shipping_method"
                    checked={selectedCarrier === "manual" && selectedManualId === m.id}
                    onClick={() => {
                      if (readOnly || isDisabled) return;
                      userTouchedSelection.current = true;
                      setSelectedCarrier("manual");
                      setSelectedManualId(m.id);
                      setSelectedUpsCode(null);
                      setSelectedIds((s) => ({ ...s, manual_shipping_id: m.id, ups_service_code: null }));
                    }}
                    disabled={readOnly || isDisabled}
                  />
                  <div>
                    <div className="fw-semibold">{m.name}</div>
                    {selectedCarrier === "manual" && selectedManualId === m.id && aboveNote && (
                      <div className="small text-muted mt-1">{aboveNote}</div>
                    )}
                  </div>
                </div>
                <div className="fw-bold">
                  {computeManualAmount(m as ManualOpt, totalQty, packages)}
                </div>
              </label>
            );
          })}





          {/* UPS options */}
          {upsConfigs.length > 0 && (
            <div className="mb-2 mt-3">
              <label className="form-label" htmlFor="ups-delivery-address-type">
                UPS delivery address type
              </label>
              <Select
                inputId="ups-delivery-address-type"
                options={UPS_ADDRESS_TYPE_OPTIONS}
                isClearable
                placeholder="- Select -"
                isDisabled={readOnly || isDisabled}
                value={
                  UPS_ADDRESS_TYPE_OPTIONS.find((option) => option.value === addressType) || null
                }
                onChange={(selected: any) => {
                  setUpsAddressType({
                    key: addressKey,
                    value: selected?.value || "saved",
                  });
                }}
                components={{ DropdownIndicator: UpsAddressTypeDropdownIndicator }}
                styles={upsAddressTypeSelectStyles}
                classNamePrefix="rs"
                menuPlacement="bottom"
              />
            </div>
          )}
          <div className="rate-scroll">
            {upsConfigs.length > 0 && !canCallRates && !upsRatesLoading && (
              <div className="small text-muted mb-2 px-1">
                Complete the ship-to address and package weight to load UPS rates.
              </div>
            )}
            {upsRatesLoading && (
              <div
                className="d-flex align-items-center gap-2 text-muted mb-2 py-2 px-1 border rounded-3 bg-white"
                role="status"
                aria-live="polite"
              >
                <div className="spinner-border spinner-border-sm text-secondary" aria-hidden="true" />
                <span>Loading UPS rates...</span>
              </div>
            )}
            {!upsRatesLoading && canCallRates && upsRates.length > 0 && (
              <div className="small ship-ups-rates-loaded mb-2 px-1 d-flex align-items-center gap-1">
                <i className="ri-checkbox-circle-line" aria-hidden="true" />
                <span>UPS rates loaded - select a service below.</span>
              </div>
            )}
            {!upsRatesLoading && canCallRates && upsRatesAttempted && upsRates.length === 0 && (
              <div className="small text-muted mb-2 px-1">
                No UPS rates returned for this shipment. You can still use a manual shipping method above.
              </div>
            )}
            {!upsRatesLoading && upsRates.map((u) => (
              <label
                key={u.service_code}
                className={`border rounded-4 p-3 mb-2 w-100 d-flex justify-content-between align-items-center ${selectedCarrier === "ups" && selectedUpsCode === u.service_code ? "border-primary bg-light" : ""
                  }`}
              >
                <div className="d-flex align-items-center">
                  <input
                    type="radio"
                    className="form-check-input me-3"
                    name="shipping_method"
                    checked={selectedCarrier === "ups" && selectedUpsCode === u.service_code}
                    onClick={() => {
                      if (readOnly || isDisabled) return;
                      userTouchedSelection.current = true;
                      setSelectedCarrier("ups");
                      const code = String(u.service_code);
                      setSelectedUpsCode(code);
                      setSelectedManualId(null);
                      setSelectedIds((s) => ({ ...s, manual_shipping_id: null, ups_service_code: code }));
                    }}
                    disabled={readOnly || isDisabled}
                  />

                  {/* logo + name side-by-side */}
                  <div className="d-flex align-items-center gap-3">
                    <img src={upslogo} alt="UPS" width={36} height={36} className="rounded-2" />
                    <div>
                      <div className="fw-semibold">
                        {u.service_name} <span className="text-muted ms-2">(UPS)</span>
                      </div>
                      <div className="small text-muted">
                        {u.delivery_days ? `${u.delivery_days} business days` : ""}
                        {u.delivery_by_time ? ` by ${u.delivery_by_time}` : ""}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fw-bold">${fmt2(u.total_charge)}</div>
              </label>
            ))}
          </div>
        </div>

        <style>{`
        .rate-scroll {
          max-height: 350px;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .rate-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .rate-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .rate-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .rate-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .ship-ups-rates-loaded {
          color: #00a63e !important;
        }
      `}</style>
      </div>
    </div>
  );
});

export default ShippingOptionsui;

